/**
 * Turnos Service - API client for turn management
 */

import { api } from "@/lib/api";

// =============================================================================
// TYPES
// =============================================================================

export interface PuntoAtencion {
  id: number;
  codigo: string;
  nombre: string;
  sucursal: number;
  sucursal_nombre: string;
  tipo: "RECEPCION" | "EXTRACCION" | "CAJA" | "ENTREGA";
  descripcion: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Turno {
  id: number;
  numero: number;
  sucursal: number;
  sucursal_nombre: string;
  fecha: string;
  hora_llegada: string;
  hora_atencion: string | null;
  hora_fin: string | null;
  estado: "ESPERANDO" | "EN_ATENCION" | "ATENDIDO" | "CANCELADO";
  estado_display: string;
  prioridad: "BAJA" | "MEDIA" | "ALTA";
  prioridad_display: string;
  prioridad_orden: number;
  punto_atencion: number | null;
  punto_atencion_nombre: string | null;
  tipo_servicio: string;
  observaciones: string;
  orden_id: number | null;
  veces_llamado: number;
  tiempo_espera: number | null;
  tiempo_atencion: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TurnoListItem {
  id: number;
  numero: number;
  sucursal: number;
  sucursal_nombre: string;
  fecha: string;
  hora_llegada: string;
  estado: "ESPERANDO" | "EN_ATENCION" | "ATENDIDO" | "CANCELADO";
  estado_display: string;
  prioridad: "BAJA" | "MEDIA" | "ALTA";
  prioridad_display: string;
  punto_atencion_nombre: string | null;
  tipo_servicio: string;
  tiempo_espera: number | null;
  veces_llamado: number;
}

export interface TurnoCreateData {
  sucursal_id: number;
  prioridad?: "BAJA" | "MEDIA" | "ALTA";
  tipo_servicio?: string;
  observaciones?: string;
}

export interface TurnoEstadisticas {
  fecha_inicio: string;
  fecha_fin: string;
  total: number;
  por_estado: Record<string, number>;
  por_prioridad: Record<string, number>;
  tiempo_espera_promedio?: number;
  tiempo_atencion_promedio?: number;
}

export interface TiempoEspera {
  sucursal_id: number;
  prioridad: string;
  tiempo_espera_minutos: number;
}

// =============================================================================
// TURNO API
// =============================================================================

/**
 * Create a new turn
 */
export async function crearTurno(data: TurnoCreateData): Promise<Turno> {
  const response = await api.post<Turno>("/turnos/", data);
  return response.data;
}

/**
 * Create a new turn (public kiosk - no auth)
 */
export async function crearTurnoPublico(data: {
  sucursal_codigo: string;
  tipo_servicio?: string;
}): Promise<Turno> {
  const response = await api.post<Turno>("/turnos/crear-publico/", data);
  return response.data;
}

/**
 * Get turn by ID
 */
export async function getTurno(id: number): Promise<Turno> {
  const response = await api.get<Turno>(`/turnos/${id}/`);
  return response.data;
}

/**
 * Get queue panel (turns waiting ordered by priority)
 */
export async function getPanelTurnos(sucursalId: number, fecha?: string): Promise<TurnoListItem[]> {
  const params: Record<string, string> = { sucursal_id: String(sucursalId) };
  if (fecha) params.fecha = fecha;

  const response = await api.get<TurnoListItem[]>("/turnos/panel/", { params });
  return response.data;
}

/**
 * Call a turn for attention
 */
export async function llamarTurno(turnoId: number, puntoAtencionId: number): Promise<Turno> {
  const response = await api.post<Turno>(`/turnos/${turnoId}/llamar/`, {
    punto_atencion_id: puntoAtencionId,
  });
  return response.data;
}

/**
 * Finish turn attention
 */
export async function finalizarTurno(turnoId: number): Promise<Turno> {
  const response = await api.post<Turno>(`/turnos/${turnoId}/finalizar/`);
  return response.data;
}

/**
 * Cancel a turn
 */
export async function cancelarTurno(turnoId: number, motivo?: string): Promise<Turno> {
  const response = await api.post<Turno>(`/turnos/${turnoId}/cancelar/`, {
    motivo: motivo || "",
  });
  return response.data;
}

/**
 * Recall a turn (increment call counter)
 */
export async function rellamarTurno(turnoId: number): Promise<Turno> {
  const response = await api.post<Turno>(`/turnos/${turnoId}/rellamar/`);
  return response.data;
}

/**
 * Update turn priority
 */
export async function updateTurnoPrioridad(
  turnoId: number,
  prioridad: "BAJA" | "MEDIA" | "ALTA"
): Promise<Turno> {
  const response = await api.patch<Turno>(`/turnos/${turnoId}/`, { prioridad });
  return response.data;
}

/**
 * Link turn to an order
 */
export async function vincularOrden(turnoId: number, ordenId: number): Promise<Turno> {
  const response = await api.post<Turno>(`/turnos/${turnoId}/vincular-orden/`, {
    orden_id: ordenId,
  });
  return response.data;
}

/**
 * Get turn statistics
 */
export async function getEstadisticas(
  sucursalId: number,
  fechaInicio?: string,
  fechaFin?: string
): Promise<TurnoEstadisticas> {
  const params: Record<string, string> = { sucursal_id: String(sucursalId) };
  if (fechaInicio) params.fecha_inicio = fechaInicio;
  if (fechaFin) params.fecha_fin = fechaFin;

  const response = await api.get<TurnoEstadisticas>("/turnos/estadisticas/", {
    params,
  });
  return response.data;
}

/**
 * Get estimated wait time
 */
export async function getTiempoEspera(
  sucursalId: number,
  prioridad?: string
): Promise<TiempoEspera> {
  const params: Record<string, string> = { sucursal_id: String(sucursalId) };
  if (prioridad) params.prioridad = prioridad;

  const response = await api.get<TiempoEspera>("/turnos/tiempo-espera/", {
    params,
  });
  return response.data;
}

// =============================================================================
// PUNTO ATENCION API
// =============================================================================

/**
 * Paginated response from DRF
 */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Fetch params for paginated requests
 */
export interface PuntoAtencionFetchParams {
  page?: number;
  page_size?: number;
  ordering?: string;
  search?: string;
  sucursal?: string;
  tipo?: string;
  is_active?: string;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Get attention points (paginated) - for useServerDataTable
 */
export async function getPuntosAtencionPaginated(
  params: PuntoAtencionFetchParams
): Promise<PaginatedResponse<PuntoAtencion>> {
  const queryParams: Record<string, string> = {};

  if (params.page) queryParams.page = String(params.page);
  if (params.page_size) queryParams.page_size = String(params.page_size);
  if (params.ordering) queryParams.ordering = params.ordering;
  if (params.search) queryParams.search = params.search;
  if (params.sucursal && params.sucursal !== "all") queryParams.sucursal_id = params.sucursal;
  if (params.tipo && params.tipo !== "all") queryParams.tipo = params.tipo;
  if (params.is_active && params.is_active !== "all") queryParams.is_active = params.is_active;

  const response = await api.get<PaginatedResponse<PuntoAtencion>>("/puntos-atencion/", {
    params: queryParams,
  });
  return response.data;
}

// =============================================================================
// PANTALLA PUBLICA API
// =============================================================================

export interface PantallaPublicaTurnoActual {
  id: number;
  numero: number;
  prioridad: string;
  punto_atencion: { id: number; codigo: string; nombre: string } | null;
  veces_llamado: number;
}

export interface PantallaPublicaTurnoCola {
  id: number;
  numero: number;
  prioridad: string;
  hora_llegada: string | null;
}

export interface PantallaPublicaTurnoAtendido {
  id: number;
  numero: number;
  hora_fin: string | null;
}

export interface PantallaPublicaData {
  sucursal: {
    id: number;
    codigo: string;
    nombre: string;
  };
  turno_actual: PantallaPublicaTurnoActual | null;
  cola: PantallaPublicaTurnoCola[];
  atendidos: PantallaPublicaTurnoAtendido[];
  timestamp: string;
}

/**
 * Get public display data for a branch (no auth required)
 */
export async function getPantallaPublica(sucursalCodigo: string): Promise<PantallaPublicaData> {
  const response = await api.get<PantallaPublicaData>(
    `/turnos/pantalla-publica/?sucursal=${encodeURIComponent(sucursalCodigo)}`
  );
  return response.data;
}

/**
 * Get attention points for a branch (legacy - returns all results)
 */
export async function getPuntosAtencion(sucursalId?: number): Promise<PuntoAtencion[]> {
  const params: Record<string, string> = {};
  if (sucursalId) params.sucursal_id = String(sucursalId);

  const response = await api.get<PaginatedResponse<PuntoAtencion>>("/puntos-atencion/", {
    params,
  });
  return response.data.results;
}

/**
 * Get active attention points for a branch
 */
export async function getPuntosAtencionActivos(sucursalId: number): Promise<PuntoAtencion[]> {
  const puntos = await getPuntosAtencion(sucursalId);
  return puntos.filter((p) => p.is_active);
}

// =============================================================================
// PUNTO ATENCION CRUD API
// =============================================================================

export interface PuntoAtencionCreateData {
  codigo: string;
  nombre: string;
  sucursal: number;
  tipo: "RECEPCION" | "EXTRACCION" | "CAJA" | "ENTREGA";
  descripcion?: string;
}

export type PuntoAtencionUpdateData = Partial<PuntoAtencionCreateData>;

/**
 * Create a new attention point
 */
export async function createPuntoAtencion(data: PuntoAtencionCreateData): Promise<PuntoAtencion> {
  const response = await api.post<PuntoAtencion>("/puntos-atencion/", data);
  return response.data;
}

/**
 * Get attention point by ID
 */
export async function getPuntoAtencion(id: number): Promise<PuntoAtencion> {
  const response = await api.get<PuntoAtencion>(`/puntos-atencion/${id}/`);
  return response.data;
}

/**
 * Update an attention point
 */
export async function updatePuntoAtencion(
  id: number,
  data: PuntoAtencionUpdateData
): Promise<PuntoAtencion> {
  const response = await api.patch<PuntoAtencion>(`/puntos-atencion/${id}/`, data);
  return response.data;
}

/**
 * Delete an attention point (hard delete)
 */
export async function deletePuntoAtencion(id: number): Promise<void> {
  await api.delete(`/puntos-atencion/${id}/`);
}

/**
 * Deactivate an attention point (soft delete)
 */
export async function deactivatePuntoAtencion(id: number): Promise<PuntoAtencion> {
  const response = await api.post<PuntoAtencion>(`/puntos-atencion/${id}/deactivate/`);
  return response.data;
}

/**
 * Activate an attention point
 */
export async function activatePuntoAtencion(id: number): Promise<PuntoAtencion> {
  const response = await api.post<PuntoAtencion>(`/puntos-atencion/${id}/activate/`);
  return response.data;
}
